"use client";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import Image from "next/image";
import onboarding1 from "@/public/assets/onboarding1.svg";
import onboarding2 from "@/public/assets/onboarding2.svg";
import onboarding3 from "@/public/assets/onboarding3.svg";
import useAutoSwipeStore from "@/app/stores/autoSwipeStore";

interface AutoSwipeRedProps {
  startIndex?: number;
}

export default function AutoSwipeRed({ startIndex = 0 }: AutoSwipeRedProps) {
  const { currentIndex, onboardingItems, setCurrentIndex } =
    useAutoSwipeStore();

  // Safely create items with images, ensuring all images are valid
  const images = [onboarding1, onboarding2, onboarding3];
  const itemsWithImages = onboardingItems
    .slice(0, 3)
    .map((item, index) => ({
      ...item,
      image: images[index] || onboarding1, // Fallback to first image if missing
    }))
    .filter((item) => item && item.image); // Only keep items with valid images

  // Ensure currentIndex is within bounds
  const safeIndex =
    itemsWithImages.length > 0
      ? Math.max(0, Math.min(currentIndex, itemsWithImages.length - 1))
      : 0;
  const currentItem = itemsWithImages[safeIndex];

  // Ensure auto-advance runs reliably without being reset on re-renders
  useEffect(() => {
    const validStartIndex = Math.max(
      0,
      Math.min(startIndex, onboardingItems.length - 1)
    );
    setCurrentIndex(validStartIndex);

    const interval = setInterval(() => {
      // Call the store directly to avoid effect re-creation on function identity changes
      useAutoSwipeStore.getState().nextSlide();
    }, 4000);

    return () => clearInterval(interval);
  }, [startIndex, setCurrentIndex, onboardingItems.length]);

  // Don't render if no valid item
  if (!currentItem || !currentItem.image) {
    return null;
  }

  return (
    <div className="md:w-1/2 hidden w-full bg-white md:flex justify-center items-stretch p-6">
      <div className="bg-primary rounded-2xl w-full flex flex-col justify-between items-center gap-8 p-8 text-white">
        <div className="text-center pt-2">
          <p className="sm:text-2xl md:text-3xl text-xl font-bold mb-4">
            {currentItem.title}
          </p>
          <p className="text-sm sm:text-base md:text-md font-medium mt-2">
            {currentItem.description}
          </p>
        </div>

        <Image
          src={currentItem.image}
          alt={`onboarding-${safeIndex + 1}`}
          width={300}
          height={300}
        />

        {/* Plan Section */}
        <div className="text-center w-2/3 pb-6">
          <p className="text-xl sm:text-2xl md:text-3xl font-semibold mb-4">
            {currentItem.description2}
          </p>
          <p className="text-md mt-2">{currentItem.description3}</p>

          {/* Auto-Swipe Indicators */}
          <div className="flex justify-center items-center mt-8 gap-2">
            {itemsWithImages.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  safeIndex === index ? "bg-white w-10" : "bg-[#D2646C] w-2"
                }`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
